import {
  buildFindingProvenance,
  FINDING_FIELD_TO_EXPLAIN_KEY,
  type FindingAdditionalInfo,
} from '../../../../helpers/findingsProvenance';
import gestationalAgeIcon from '../../../assets/images/gestational_age_blue.png';
import fetalWeightIcon from '../../../assets/images/fetal_weight_blue.png';
import fetalHeartIcon from '../../../assets/images/fetal_heart_blue.png';
import femurIcon from '../../../assets/images/femur_blue.png';
import headCircumferenceIcon from '../../../assets/images/Head_Circumference_blue.png';
import biparietalDiameterIcon from '../../../assets/images/Biparietal_Diameter_blue.png';
import abdominalIcon from '../../../assets/images/abdominal_blue.png';
import preeclampsiaIcon from '../../../assets/images/peeclampsia_blue.png';
import gestationalDiabetesIcon from '../../../assets/images/gestational_diabetes_blue.png';
import fetalSexIcon from '../../../assets/images/fetal_sex_blue.png';
import { OPTIONS, type SelectOption } from "../../report/lib/form-config";

// Presentation images
import cephalicIcon from '../../../assets/images/cephalic_blue.png';
import breechIcon from '../../../assets/images/breech_blue.png';
import traverseIcon from '../../../assets/images/traverse_blue.png';

// Placenta localization images
import anteriorIcon from '../../../assets/images/Anteriror_blue.png';
import posteriorIcon from '../../../assets/images/posterior_blue.png';
import fundalIcon from '../../../assets/images/fundal_blue.png';

// Placenta distance images
import lowLyingIcon from '../../../assets/images/low_lying_blue.png';

export interface Finding {
  id: string;
  serieId: string;
  serieFullId: string;
  category: string;
  title: string;
  result: string;
  status: string;
  description: string;
  rawData: any;
  fieldKey: string;
  isAvailable: boolean;
  isInReport?: boolean;
  isReportSpecific?: boolean;
}

export interface FindingTemplate {
  fieldKey: string;
  category: string;
  title: string;
  description: string;
  getResult: (finding: any) => string;
  getStatus: (finding: any) => string;
}

export interface SeriesData {
  id: string;
  displayId: string;
  type: string; // Add the series type field (BLIND, OB, etc.)
  findingsCount: number;
  availableCount: number;
  unavailableCount: number;
  createdAt: string;
  findings: Finding[];
}

export type ReportEditControl = {
  backendKey: string;
  type: "select" | "number";
  options?: readonly SelectOption[];
  placeholder?: string;
  step?: string;
  min?: string;
  parse?: (value: string) => unknown;
};

export type EFWValue = {
  label: string;
  value: number;
  formula: string;
};

type ReportFindingPayloadSource = Finding & {
  efwSpecific?: EFWValue;
};

export const booleanOptions = [
  { value: "true", label: "Positive" },
  { value: "false", label: "Negative" },
] as const;

export const singletonOptions = [
  { value: "false", label: "Singleton" },
  { value: "true", label: "Multiple" },
] as const;

export const sexPredictionOptions = [
  { value: "female", label: "Female" },
  { value: "male", label: "Male" },
  { value: "unknown", label: "Unknown" },
] as const;

export const parseNullableString = (value: string) => {
  const trimmed = value.trim();
  return trimmed === "" ? null : trimmed;
};

export const parseNullableNumber = (value: string) => {
  const trimmed = value.trim();
  if (trimmed === "") {
    return null;
  }
  const parsed = Number(trimmed);
  return Number.isFinite(parsed) ? parsed : null;
};

export const parseNullableBoolean = (value: string) => {
  if (value === "") {
    return null;
  }
  return value === "true";
};

export const REPORT_EDIT_CONTROLS: Record<string, ReportEditControl> = {
  presentation: {
    backendKey: "presentation",
    type: "select",
    options: OPTIONS.fetalPresentation,
    parse: parseNullableString,
  },
  placenta_localization: {
    backendKey: "placenta_localization",
    type: "select",
    options: OPTIONS.placentalLocation,
    parse: parseNullableString,
  },
  placenta_distance_to_cervix_class: {
    backendKey: "placenta_distance_to_cervix_class",
    type: "select",
    options: OPTIONS.placentaCervixClass,
    parse: parseNullableString,
  },
  heart_rate: {
    backendKey: "heart_rate",
    type: "number",
    placeholder: "bpm",
    step: "1",
    min: "0",
    parse: parseNullableNumber,
  },
  pre_eclampsia: {
    backendKey: "pre_eclampsia",
    type: "select",
    options: booleanOptions,
    parse: parseNullableBoolean,
  },
  gestational_diabetes: {
    backendKey: "gestational_diabetes",
    type: "select",
    options: booleanOptions,
    parse: parseNullableBoolean,
  },
  sex_prediction: {
    backendKey: "sex_prediction",
    type: "select",
    options: sexPredictionOptions,
    parse: parseNullableString,
  },
  is_twins: {
    backendKey: "is_twins",
    type: "select",
    options: singletonOptions,
    parse: parseNullableBoolean,
  },
  meas_hc: {
    backendKey: "hc_cm",
    type: "number",
    placeholder: "cm",
    step: "0.1",
    min: "0",
    parse: parseNullableNumber,
  },
  meas_bpd: {
    backendKey: "bpd_cm",
    type: "number",
    placeholder: "cm",
    step: "0.1",
    min: "0",
    parse: parseNullableNumber,
  },
  meas_ac: {
    backendKey: "ac_cm",
    type: "number",
    placeholder: "cm",
    step: "0.1",
    min: "0",
    parse: parseNullableNumber,
  },
  meas_fl: {
    backendKey: "fl_cm",
    type: "number",
    placeholder: "cm",
    step: "0.1",
    min: "0",
    parse: parseNullableNumber,
  },
};

export const REPORT_EDIT_CONTROLS_BY_BACKEND_KEY = Object.values(
  REPORT_EDIT_CONTROLS
).reduce<Record<string, ReportEditControl>>((acc, control) => {
  acc[control.backendKey] = control;
  return acc;
}, {});

export const ICON_MAP = {
  gestationalAge: gestationalAgeIcon,
  fetalWeight: fetalWeightIcon,
  fetalHeart: fetalHeartIcon,
  femur: femurIcon,
  headCircumference: headCircumferenceIcon,
  biparietalDiameter: biparietalDiameterIcon,
  abdominal: abdominalIcon,
  preeclampsia: preeclampsiaIcon,
  gestationalDiabetes: gestationalDiabetesIcon,
  fetalSex: fetalSexIcon,
  lowLying: lowLyingIcon,

  // Presentation specific icons
  cephalic: cephalicIcon,
  breech: breechIcon,
  transverse: traverseIcon,

  // Placenta localization icons
  anterior: anteriorIcon,
  posterior: posteriorIcon,
  fundal: fundalIcon,
};

export const CATEGORY_ICON_MAP: Record<string, string> = {
  "Gestational Age": ICON_MAP.gestationalAge,
  "Estimated Fetal Weight": ICON_MAP.fetalWeight,
  "Placenta-Cervix Distance": ICON_MAP.lowLying,
  "Categorical Predictions": ICON_MAP.cephalic,
  "Numerical Aggregations": ICON_MAP.fetalHeart,
  "Biometry Measurements": ICON_MAP.femur,
};

// Function to check if a finding is already in the study report
export const isFindingInReport = (finding: Finding, studyReport: any): boolean => {
  if (!studyReport || !finding.rawData) return false;

  const { fieldKey, rawData } = finding;

  switch (fieldKey) {
    case 'heart_rate':
      return studyReport.heart_rate !== null && studyReport.heart_rate !== undefined && 
             rawData.heart_rate !== null && rawData.heart_rate !== undefined &&
             Math.abs(studyReport.heart_rate - rawData.heart_rate) < 0.01;
    
    case 'presentation':
      return studyReport.presentation === rawData.presentation && 
             rawData.presentation !== null;
    
    case 'pre_eclampsia':
      return studyReport.pre_eclampsia === rawData.pre_eclampsia && 
             rawData.pre_eclampsia !== null && rawData.pre_eclampsia !== undefined;
    
    case 'sex_prediction':
      return studyReport.sex_prediction === rawData.sex_prediction && 
             rawData.sex_prediction !== null;
    
    case 'meas_hc':
      return studyReport.hc_cm !== null && studyReport.hc_cm !== undefined && 
             rawData.meas_hc !== null && rawData.meas_hc !== undefined &&
             Math.abs(studyReport.hc_cm - rawData.meas_hc) < 0.01;
    
    case 'meas_bpd':
      return studyReport.bpd_cm !== null && studyReport.bpd_cm !== undefined && 
             rawData.meas_bpd !== null && rawData.meas_bpd !== undefined &&
             Math.abs(studyReport.bpd_cm - rawData.meas_bpd) < 0.01;
    
    case 'meas_ac':
      return studyReport.ac_cm !== null && studyReport.ac_cm !== undefined && 
             rawData.meas_ac !== null && rawData.meas_ac !== undefined &&
             Math.abs(studyReport.ac_cm - rawData.meas_ac) < 0.01;
    
    case 'meas_fl':
      return studyReport.fl_cm !== null && studyReport.fl_cm !== undefined && 
             rawData.meas_fl !== null && rawData.meas_fl !== undefined &&
             Math.abs(studyReport.fl_cm - rawData.meas_fl) < 0.01;
    
    case 'estimated_fetal_weight':
      return studyReport.pred_efw_g !== null && studyReport.pred_efw_g !== undefined && 
             (rawData.efw_hadlock_ii || rawData.efw_hadlock_iii || 
              rawData.efw_hadlock_iv || rawData.efw_hadlock_v);
    
    case 'placenta_localization':
      return studyReport.placenta_localization === rawData.placenta_localization && 
             rawData.placenta_localization !== null;
    
    case 'placenta_distance_to_cervix_class':
      return studyReport.placenta_distance_to_cervix_class === rawData.placenta_distance_to_cervix_class && 
             rawData.placenta_distance_to_cervix_class !== null;
    
    case 'gestational_diabetes':
      return studyReport.gestational_diabetes === rawData.gestational_diabetes && 
             rawData.gestational_diabetes !== null && rawData.gestational_diabetes !== undefined;
    
    case 'is_twins':
      return studyReport.is_twins === rawData.is_twins && 
             rawData.is_twins !== null && rawData.is_twins !== undefined;

    case 'ga_aua':
      return studyReport.pred_ga_aua !== null && studyReport.pred_ga_aua !== undefined && 
             rawData.ga_aua !== null && rawData.ga_aua !== undefined &&
             Math.abs(studyReport.pred_ga_aua - rawData.ga_aua) < 0.01;
    
    case 'pred_edd':
      return studyReport.pred_edd !== null && studyReport.pred_edd !== undefined;
    
    default:
      return false;
  }
};

export const ALL_POSSIBLE_FINDINGS: FindingTemplate[] = [
  {
    fieldKey: "estimated_fetal_weight",
    category: "Estimated Fetal Weight",
    title: "Estimated Fetal Weight",
    description: "Estimated fetal weight using various Hadlock formulas based on available biometric measurements.",
    getResult: (finding: any) => {
      const hasAnyEfw = finding.efw_hadlock_ii || finding.efw_hadlock_iii || 
                       finding.efw_hadlock_iv || finding.efw_hadlock_v;
      return hasAnyEfw ? "Available" : "N/A";
    },
    getStatus: (finding: any) => {
      const hasAnyEfw = finding.efw_hadlock_ii || finding.efw_hadlock_iii || 
                       finding.efw_hadlock_iv || finding.efw_hadlock_v;
      return hasAnyEfw ? "NORMAL" : "OBSERVATION";
    },
  },
  {
    fieldKey: "ga_aua",
    category: "Gestational Age",
    title: "Average Ultrasound Age (AUA)",
    description: "Average gestational age calculated from available biometric measurements using AUA method.",
    getResult: (finding: any) => {
      if (!finding.ga_aua) return "N/A";
      const weeks = Math.floor(finding.ga_aua);
      const days = Math.round((finding.ga_aua - weeks) * 7);
      return `${weeks}w ${days}d`;
    },
    getStatus: (finding: any) => (finding.ga_aua ? "NORMAL" : "OBSERVATION"),
  },
  {
    fieldKey: "pred_edd",
    category: "Gestational Age",
    title: "Estimated Due Date",
    description: "Predicted delivery date based on gestational age calculations. This value is calculated automatically from biometric measurements.",
    getResult: (finding: any) => {
      // This field only exists in the report, not in individual findings
      // If we have gestational age data, calculate the due date
      if (finding.ga_aua) {
        const currentDate = new Date();
        const gestationalAgeWeeks = finding.ga_aua;
        const remainingWeeks = 40 - gestationalAgeWeeks;
        const dueDateTimestamp = currentDate.getTime() + (remainingWeeks * 7 * 24 * 60 * 60 * 1000);
        const dueDate = new Date(dueDateTimestamp);
        return dueDate.toLocaleDateString('en-US', { 
          year: 'numeric', 
          month: 'long', 
          day: 'numeric' 
        });
      }
      return "Calculated in Report";
    },
    getStatus: (finding: any) => "OBSERVATION",
  },
  {
    fieldKey: "presentation",
    category: "Categorical Predictions",
    title: "Fetal Presentation",
    description: "Baby's position in the uterus (cephalic, breech, transverse).",
    getResult: (finding: any) =>
      finding.presentation
        ? finding.presentation.charAt(0).toUpperCase() + finding.presentation.slice(1)
        : "N/A",
    getStatus: (finding: any) =>
      finding.presentation ? (finding.presentation === "cephalic" ? "NORMAL" : "OBSERVATION") : "OBSERVATION",
  },
  {
    fieldKey: "placenta_localization",
    category: "Categorical Predictions",
    title: "Placenta Localization",
    description: "Location of the placenta on the uterine wall.",
    getResult: (finding: any) =>
      finding.placenta_localization
        ? finding.placenta_localization.charAt(0).toUpperCase() + finding.placenta_localization.slice(1)
        : "N/A",
    getStatus: (finding: any) => (finding.placenta_localization ? "OBSERVATION" : "OBSERVATION"),
  },
  {
    fieldKey: "heart_rate",
    category: "Numerical Aggregations",
    title: "Fetal Heart Rate",
    description: "Fetal heart rate measurement (normal range: 110-160 bpm).",
    getResult: (finding: any) => (finding.heart_rate ? `${Math.round(finding.heart_rate)} bpm` : "N/A"),
    getStatus: (finding: any) => {
      if (!finding.heart_rate) return "OBSERVATION"
      const heartRate = Math.round(finding.heart_rate)
      return heartRate >= 110 && heartRate <= 160 ? "NORMAL" : "ABNORMAL"
    },
  },
  {
    fieldKey: "placenta_distance_to_cervix_class",
    category: "Placenta-Cervix Distance",
    title: "Placenta-Cervix Classification",
    description: "Distance classification between placenta and cervix.",
    getResult: (finding: any) =>
      finding.placenta_distance_to_cervix_class
        ? finding.placenta_distance_to_cervix_class.charAt(0).toUpperCase() +
        finding.placenta_distance_to_cervix_class.slice(1)
        : "N/A",
    getStatus: (finding: any) =>
      finding.placenta_distance_to_cervix_class
        ? finding.placenta_distance_to_cervix_class === "normal"
          ? "NORMAL"
          : "ABNORMAL"
        : "OBSERVATION",
  },
  {
    fieldKey: "pre_eclampsia",
    category: "Numerical Aggregations",
    title: "Preeclampsia Screening",
    description: "Screening result for preeclampsia risk.",
    getResult: (finding: any) =>
      finding.pre_eclampsia !== undefined && finding.pre_eclampsia !== null
        ? (finding.pre_eclampsia ? "Positive" : "Negative")
        : "N/A",
    getStatus: (finding: any) =>
      finding.pre_eclampsia !== undefined && finding.pre_eclampsia !== null
        ? (finding.pre_eclampsia ? "ABNORMAL" : "NORMAL")
        : "OBSERVATION",
  },
  {
    fieldKey: "gestational_diabetes",
    category: "Numerical Aggregations",
    title: "Gestational Diabetes Screening",
    description: "Screening result for gestational diabetes risk.",
    getResult: (finding: any) =>
      finding.gestational_diabetes !== undefined && finding.gestational_diabetes !== null
        ? finding.gestational_diabetes
          ? "Positive"
          : "Negative"
        : "N/A",
    getStatus: (finding: any) =>
      finding.gestational_diabetes !== undefined && finding.gestational_diabetes !== null
        ? finding.gestational_diabetes
          ? "ABNORMAL"
          : "NORMAL"
        : "OBSERVATION",
  },
  {
    fieldKey: "sex_prediction",
    category: "Categorical Predictions",
    title: "Fetal Sex Prediction",
    description: "Predicted fetal sex based on ultrasound analysis.",
    getResult: (finding: any) =>
      finding.sex_prediction
        ? finding.sex_prediction.charAt(0).toUpperCase() + finding.sex_prediction.slice(1)
        : "N/A",
    getStatus: (finding: any) => "OBSERVATION",
  },
  {
    fieldKey: "is_twins",
    category: "Categorical Predictions",
    title: "Multiple Pregnancy",
    description: "Detection of multiple fetuses (twins, triplets, etc.).",
    getResult: (finding: any) =>
      finding.is_twins !== undefined && finding.is_twins !== null
        ? (finding.is_twins ? "Multiple" : "Singleton")
        : "N/A",
    getStatus: (finding: any) => "OBSERVATION",
  },
  {
    fieldKey: "meas_ac",
    category: "Biometry Measurements",
    title: "Abdominal Circumference (cm)",
    description: "Abdominal circumference measurement for fetal growth assessment.",
    getResult: (finding: any) => {
      if (!finding.meas_ac) return "N/A";
      let result = `${finding.meas_ac.toFixed(1)} cm`;
      if (finding.ga_ac_1984) {
        const weeks = Math.floor(finding.ga_ac_1984);
        const days = Math.round((finding.ga_ac_1984 - weeks) * 7);
      }
      return result;
    },
    getStatus: (finding: any) => (finding.meas_ac ? "NORMAL" : "OBSERVATION"),
  },
  {
    fieldKey: "meas_hc",
    category: "Biometry Measurements",
    title: "Head Circumference (cm)",
    description: "Head circumference measurement for fetal growth assessment.",
    getResult: (finding: any) => {
      if (!finding.meas_hc) return "N/A";
      let result = `${finding.meas_hc.toFixed(1)} cm`;
      if (finding.ga_hc_1984) {
        const weeks = Math.floor(finding.ga_hc_1984);
        const days = Math.round((finding.ga_hc_1984 - weeks) * 7);
      }
      return result;
    },
    getStatus: (finding: any) => (finding.meas_hc ? "NORMAL" : "OBSERVATION"),
  },
  {
    fieldKey: "meas_bpd",
    category: "Biometry Measurements",
    title: "Biparietal Diameter (cm)",
    description: "Biparietal diameter measurement for fetal growth assessment.",
    getResult: (finding: any) => {
      if (!finding.meas_bpd) return "N/A";
      let result = `${finding.meas_bpd.toFixed(1)} cm`;
      if (finding.ga_bpd_1984) {
        const weeks = Math.floor(finding.ga_bpd_1984);
        const days = Math.round((finding.ga_bpd_1984 - weeks) * 7);
      }
      return result;
    },
    getStatus: (finding: any) => (finding.meas_bpd ? "NORMAL" : "OBSERVATION"),
  },
  {
    fieldKey: "meas_fl",
    category: "Biometry Measurements",
    title: "Femur Length (cm)",
    description: "Femur length measurement for fetal growth assessment.",
    getResult: (finding: any) => {
      if (!finding.meas_fl) return "N/A";
      let result = `${finding.meas_fl.toFixed(1)} cm`;
      if (finding.ga_fl_1984) {
        const weeks = Math.floor(finding.ga_fl_1984);
        const days = Math.round((finding.ga_fl_1984 - weeks) * 7);
      }
      return result;
    },
    getStatus: (finding: any) => (finding.meas_fl ? "NORMAL" : "OBSERVATION"),
  },
  
  
];

/** Build report data shape so getResult()/getGestationalAge() can read it. The study_report row
 *  stores biometry as hc_cm/bpd_cm/ac_cm/fl_cm, gestational ages as ga_hc/ga_bpd/ga_ac/ga_fl/ga_aua,
 *  and the estimated fetal weight as efw_g — so alias them onto the keys the templates expect. */
const reportDataFromStudyReport = (studyReport: any): any => {
  if (!studyReport) return {};
  return {
    ...studyReport,
    meas_hc: studyReport.hc_cm,
    meas_bpd: studyReport.bpd_cm,
    meas_ac: studyReport.ac_cm,
    meas_fl: studyReport.fl_cm,
    ga_hc_1984: studyReport.ga_hc,
    ga_bpd_1984: studyReport.ga_bpd,
    ga_ac_1984: studyReport.ga_ac,
    ga_fl_1984: studyReport.ga_fl,
    ga_aua: studyReport.ga_aua,
    efw_hadlock_ii: studyReport.efw_g,
    efw_hadlock_iii: studyReport.efw_g,
    efw_hadlock_iv: studyReport.efw_g,
    efw_hadlock_v: studyReport.efw_g,
  };
};

/** Resolve DB findings.id for collaboration (one row per series in findings table). */
export function resolveFindingDbId(finding: Finding): number | undefined {
  const id = finding.rawData?.id;
  if (id == null || id === '') return undefined;
  const numeric = Number(id);
  return Number.isFinite(numeric) ? numeric : undefined;
}

function findDbIdForField(fieldKey: string, allRawFindings: any[]): number | undefined {
  const template = ALL_POSSIBLE_FINDINGS.find((t) => t.fieldKey === fieldKey);
  if (!template) return undefined;

  for (const row of allRawFindings) {
    if (row?.id != null && template.getResult(row) !== 'N/A') {
      return Number(row.id);
    }
  }

  const anyRow = allRawFindings.find((row) => row?.id != null);
  return anyRow?.id != null ? Number(anyRow.id) : undefined;
}

/** Report tab: one card per finding type built from study_report only (no looping through series =
 *  no duplication). Every template is rendered — including biometry — so the report view mirrors the
 *  full card set of the series views; cards without a value are shown as N/A (isAvailable=false). */
export const getReportFindingsFromStudyReport = (
  studyReport: any,
  allRawFindings: any[] = []
): Finding[] => {
  if (!studyReport) return [];
  const reportData = reportDataFromStudyReport(studyReport);

  return ALL_POSSIBLE_FINDINGS.map((findingTemplate) => {
    const result = findingTemplate.getResult(reportData);
    const dbId = findDbIdForField(findingTemplate.fieldKey, allRawFindings);

    const finding: Finding = {
      id: `report_${findingTemplate.fieldKey}`,
      serieId: 'report',
      serieFullId: 'report',
      category: findingTemplate.category,
      title: findingTemplate.title,
      result,
      status: findingTemplate.getStatus(reportData),
      description: findingTemplate.description,
      rawData: dbId != null ? { ...reportData, id: dbId } : reportData,
      fieldKey: findingTemplate.fieldKey,
      isAvailable: result !== 'N/A',
      isInReport: true,
    };
    return finding;
  });
};

export const processFindingsData = (apiData: any): {
  findingsBySeries: Record<string, Finding[]>;
  findingsByType: Record<string, Finding[]>;
  series: SeriesData[];
} => {
  if (!apiData?.findings) {
    return { findingsBySeries: {}, findingsByType: {}, series: [] };
  }

  const findingsBySeries: Record<string, Finding[]> = {};
  const findingsByType: Record<string, Finding[]> = {};
  const studyReport = apiData.study_report;

  apiData.findings.forEach((rawFinding: any) => {
    const serieId = rawFinding.serie_instance_uid.slice(-8);
    const serieFullId = rawFinding.serie_instance_uid;

    if (!findingsBySeries[serieFullId]) {
      findingsBySeries[serieFullId] = [];
    }

    ALL_POSSIBLE_FINDINGS.forEach((findingTemplate) => {
      // Skip pred_edd for individual series findings as it only exists in the report
      if (findingTemplate.fieldKey === "pred_edd") {
        return;
      }

      const finding: Finding = {
        id: `${findingTemplate.fieldKey}_${rawFinding.id}_${serieId}`,
        serieId,
        serieFullId,
        category: findingTemplate.category,
        title: findingTemplate.title,
        result: findingTemplate.getResult(rawFinding),
        status: findingTemplate.getStatus(rawFinding),
        description: findingTemplate.description,
        rawData: rawFinding,
        fieldKey: findingTemplate.fieldKey,
        isAvailable: findingTemplate.getResult(rawFinding) !== "N/A",
        isInReport: isFindingInReport({
          id: `${findingTemplate.fieldKey}_${rawFinding.id}_${serieId}`,
          serieId,
          serieFullId,
          category: findingTemplate.category,
          title: findingTemplate.title,
          result: findingTemplate.getResult(rawFinding),
          status: findingTemplate.getStatus(rawFinding),
          description: findingTemplate.description,
          rawData: rawFinding,
          fieldKey: findingTemplate.fieldKey,
          isAvailable: findingTemplate.getResult(rawFinding) !== "N/A",
        }, studyReport),
      };

      findingsBySeries[serieFullId].push(finding);

      const typeKey = `${findingTemplate.fieldKey}_${findingTemplate.category}`;
      if (!findingsByType[typeKey]) {
        findingsByType[typeKey] = [];
      }
      findingsByType[typeKey].push(finding);
    });
  });

  const series = Object.keys(findingsBySeries)
    .map((serieFullId) => {
      const findings = findingsBySeries[serieFullId];
      const serieId = findings[0]?.serieId || serieFullId.slice(-8);
      const serieType = findings[0]?.rawData?.serie?.type; // Extract the series type from nested serie object
      const availableFindings = findings.filter((f) => f.isAvailable);
      const createdAt = findings[0]?.rawData?.created_at;

      return {
        id: serieFullId,
        displayId: serieId,
        type: serieType || "Unknown", // Assign the extracted type
        findingsCount: findings.length,
        availableCount: availableFindings.length,
        unavailableCount: findings.length - availableFindings.length,
        createdAt: createdAt ? new Date(createdAt).toLocaleDateString() : "Unknown",
        findings,
      };
    })
    .sort((a, b) => {
      const aDate = a.findings[0]?.rawData?.created_at;
      const bDate = b.findings[0]?.rawData?.created_at;
      if (!aDate || !bDate) return 0;
      return new Date(bDate).getTime() - new Date(aDate).getTime();
    });

  return { findingsBySeries, findingsByType, series };
};

// Get current series findings utility
export const getCurrentSeriesFindings = (
  activeSeries: string,
  findingsBySeries: Record<string, Finding[]>,
  findingsByType: Record<string, Finding[]>,
  findingSeriesIndex: Record<string, number>
): Finding[] => {
  if (activeSeries === "All") {
    const uniqueFindings: Finding[] = [];
    const seenFindingTypes = new Set<string>();

    ALL_POSSIBLE_FINDINGS.forEach((findingTemplate) => {
      const typeKey = `${findingTemplate.fieldKey}_${findingTemplate.category}`;

      if (!seenFindingTypes.has(typeKey)) {
        seenFindingTypes.add(typeKey);

        const findingsOfType = findingsByType[typeKey] || [];

        if (findingsOfType.length > 0) {
          const currentIndex = findingSeriesIndex[typeKey] || 0;
          const validIndex = Math.min(currentIndex, findingsOfType.length - 1);
          const selectedFinding = findingsOfType[validIndex];

          if (selectedFinding) {
            uniqueFindings.push(selectedFinding);
          }
        }
      }
    });

    return uniqueFindings;
  }

  if (!activeSeries || !findingsBySeries[activeSeries]) return [];
  return findingsBySeries[activeSeries];
};

export const filterFindings = (
  findings: Finding[],
  selectedCategories: string[]
): Finding[] => {
  if (selectedCategories.length === 0) {
    return findings;
  }
  return findings.filter((finding) => selectedCategories.includes(finding.category));
};

export const getUniqueCategories = (findings: Finding[]): string[] => {
  return Array.from(new Set(findings.map((finding) => finding.category)));
};

export const getReportEditValue = (
  finding: Finding,
  control: ReportEditControl,
  reportEdits: Record<string, string>
) => {
  if (Object.prototype.hasOwnProperty.call(reportEdits, control.backendKey)) {
    return reportEdits[control.backendKey];
  }

  const rawValueByField: Record<string, unknown> = {
    presentation: finding.rawData?.presentation,
    placenta_localization: finding.rawData?.placenta_localization,
    placenta_distance_to_cervix_class:
      finding.rawData?.placenta_distance_to_cervix_class,
    heart_rate: finding.rawData?.heart_rate,
    pre_eclampsia: finding.rawData?.pre_eclampsia,
    gestational_diabetes: finding.rawData?.gestational_diabetes,
    sex_prediction: finding.rawData?.sex_prediction,
    is_twins: finding.rawData?.is_twins,
    meas_hc: finding.rawData?.meas_hc,
    meas_bpd: finding.rawData?.meas_bpd,
    meas_ac: finding.rawData?.meas_ac,
    meas_fl: finding.rawData?.meas_fl,
    estimated_fetal_weight:
      finding.rawData?.pred_efw_g ??
      finding.rawData?.efw_hadlock_ii ??
      finding.rawData?.efw_hadlock_iii ??
      finding.rawData?.efw_hadlock_iv ??
      finding.rawData?.efw_hadlock_v,
    ga_aua: finding.rawData?.ga_aua ?? finding.rawData?.pred_ga_aua,
  };

  const value =
    finding.rawData?.[control.backendKey] ?? rawValueByField[finding.fieldKey];
  return value === null || value === undefined ? "" : String(value);
};

export const getGestationalAge = (finding: Finding) => {
  const { fieldKey, rawData } = finding;
  let gaField = null;

  switch (fieldKey) {
    case 'meas_ac':
      gaField = rawData.ga_ac_1984;
      break;
    case 'meas_hc':
      gaField = rawData.ga_hc_1984;
      break;
    case 'meas_bpd':
      gaField = rawData.ga_bpd_1984;
      break;
    case 'meas_fl':
      gaField = rawData.ga_fl_1984;
      break;
  }

  if (gaField) {
    const weeks = Math.floor(gaField);
    const days = Math.round((gaField - weeks) * 7);
    return { weeks, days, hasGA: true };
  }

  return { hasGA: false };
};

export const getAUAGestationalAge = (finding: Finding) => {
  const { fieldKey, rawData } = finding;
  if (fieldKey === 'ga_aua' && rawData.ga_aua) {
    const weeks = Math.floor(rawData.ga_aua);
    const days = Math.round((rawData.ga_aua - weeks) * 7);
    return { weeks, days, hasAUA: true };
  }
  return { hasAUA: false };
};

export const getEFWValues = (finding: Finding): EFWValue[] => {
  const { fieldKey, rawData } = finding;
  if (fieldKey !== 'estimated_fetal_weight') return [];

  const efwValues: EFWValue[] = [];

  if (rawData.efw_hadlock_ii) {
    efwValues.push({
      label: 'Hadlock II',
      value: Math.round(rawData.efw_hadlock_ii),
      formula: 'BPD, HC, AC, FL'
    });
  }
  if (rawData.efw_hadlock_iii) {
    efwValues.push({
      label: 'Hadlock III',
      value: Math.round(rawData.efw_hadlock_iii),
      formula: 'BPD, AC, FL'
    });
  }
  if (rawData.efw_hadlock_iv) {
    efwValues.push({
      label: 'Hadlock IV',
      value: Math.round(rawData.efw_hadlock_iv),
      formula: 'HC, AC, FL'
    });
  }
  if (rawData.efw_hadlock_v) {
    efwValues.push({
      label: 'Hadlock V',
      value: Math.round(rawData.efw_hadlock_v),
      formula: 'AC, FL'
    });
  }

  return efwValues;
};

export const getSelectedFindingDetails = (
  selectedFindings: string[],
  currentSeriesFindings: Finding[]
): ReportFindingPayloadSource[] => {
  const selectedFindingDetails: ReportFindingPayloadSource[] = [];

  selectedFindings.forEach((selectedId) => {
    if (selectedId.includes('_hadlock_')) {
      const parts = selectedId.split('_hadlock_');
      const originalFindingId = parts[0] + '_estimated_fetal_weight_' + parts[0].split('_').slice(-1)[0];
      const efwType = 'hadlock_' + parts[1];

      const originalFinding = currentSeriesFindings.find((finding) => finding.id === originalFindingId);
      if (originalFinding) {
        const efwValues = getEFWValues(originalFinding);
        const specificEfw = efwValues.find((efw) => efw.label.toLowerCase().replace(' ', '_') === efwType);

        if (specificEfw) {
          selectedFindingDetails.push({
            ...originalFinding,
            id: selectedId,
            title: `${originalFinding.title} - ${specificEfw.label}`,
            result: `${specificEfw.value}g`,
            description: `${originalFinding.description} Formula: ${specificEfw.formula}`,
            efwSpecific: specificEfw
          });
        }
      }
      return;
    }

    const finding = currentSeriesFindings.find((currentFinding) => currentFinding.id === selectedId);
    if (finding) {
      selectedFindingDetails.push(finding);
    }
  });

  return selectedFindingDetails;
};

export const buildReportFindingsPayload = ({
  studyInstanceUid,
  activeSeries,
  canUpdateActiveSeries,
  selectedFindingDetails,
  reportEdits,
}: {
  studyInstanceUid: string;
  activeSeries: string;
  canUpdateActiveSeries: boolean;
  selectedFindingDetails: ReportFindingPayloadSource[];
  reportEdits: Record<string, string>;
}) => {
  const findingsData: Record<string, unknown> = {
    study_instance_uid: studyInstanceUid,
  };

  if (activeSeries && canUpdateActiveSeries) {
    findingsData.serie_instance_uid = activeSeries;
  }

  selectedFindingDetails.forEach((finding) => {
    const { fieldKey, rawData } = finding;

    switch (fieldKey) {
      case 'heart_rate':
        if (rawData.heart_rate) {
          findingsData.heart_rate = parseFloat(rawData.heart_rate);
        }
        break;
      case 'presentation':
        if (rawData.presentation) {
          findingsData.presentation = rawData.presentation;
        }
        break;
      case 'pre_eclampsia':
        if (rawData.pre_eclampsia !== undefined) {
          findingsData.pre_eclampsia = rawData.pre_eclampsia;
        }
        break;
      case 'sex_prediction':
        if (rawData.sex_prediction) {
          findingsData.sex_prediction = rawData.sex_prediction;
        }
        break;
      case 'meas_hc':
        if (rawData.meas_hc) {
          findingsData.hc_cm = parseFloat(rawData.meas_hc);
          if (rawData.ga_hc_1984) {
            findingsData.ga_hc = parseFloat(rawData.ga_hc_1984);
          }
        }
        break;
      case 'meas_bpd':
        if (rawData.meas_bpd) {
          findingsData.bpd_cm = parseFloat(rawData.meas_bpd);
          if (rawData.ga_bpd_1984) {
            findingsData.ga_bpd = parseFloat(rawData.ga_bpd_1984);
          }
        }
        break;
      case 'meas_ac':
        if (rawData.meas_ac) {
          findingsData.ac_cm = parseFloat(rawData.meas_ac);
          if (rawData.ga_ac_1984) {
            findingsData.ga_ac = parseFloat(rawData.ga_ac_1984);
          }
        }
        break;
      case 'meas_fl':
        if (rawData.meas_fl) {
          findingsData.fl_cm = parseFloat(rawData.meas_fl);
          if (rawData.ga_fl_1984) {
            findingsData.ga_fl = parseFloat(rawData.ga_fl_1984);
          }
        }
        break;
      case 'estimated_fetal_weight':
        if (finding.efwSpecific) {
          findingsData.efw_g = finding.efwSpecific.value;
        } else if (rawData.efw_hadlock_ii) {
          findingsData.efw_g = parseFloat(rawData.efw_hadlock_ii);
        }
        break;
      case 'ga_aua':
        if (rawData.ga_aua) {
          findingsData.ga_aua = parseFloat(rawData.ga_aua);
        }
        break;
      case 'pred_edd':
        break;
      case 'placenta_localization':
        if (rawData.placenta_localization) {
          findingsData.placenta_localization = rawData.placenta_localization;
        }
        break;
      case 'placenta_distance_to_cervix_class':
        if (rawData.placenta_distance_to_cervix_class) {
          findingsData.placenta_distance_to_cervix_class = rawData.placenta_distance_to_cervix_class;
        }
        break;
      case 'gestational_diabetes':
        if (rawData.gestational_diabetes !== undefined) {
          findingsData.gestational_diabetes = rawData.gestational_diabetes;
        }
        break;
      case 'is_twins':
        if (rawData.is_twins !== undefined) {
          findingsData.is_twins = rawData.is_twins;
        }
        break;
    }
  });

  Object.entries(reportEdits).forEach(([backendKey, value]) => {
    const control = REPORT_EDIT_CONTROLS_BY_BACKEND_KEY[backendKey];
    findingsData[backendKey] = control?.parse
      ? control.parse(value)
      : parseNullableString(value);
  });

  const additionalInfo: FindingAdditionalInfo = {};
  selectedFindingDetails.forEach((finding) => {
    const explainKey = FINDING_FIELD_TO_EXPLAIN_KEY[finding.fieldKey];
    if (!explainKey || additionalInfo[explainKey]) return;
    const serieBlocks = finding.rawData?.additional_info as FindingAdditionalInfo | undefined;
    const block = serieBlocks?.[explainKey];
    if (block && typeof block === 'object') {
      additionalInfo[explainKey] = block;
    }
  });

  const manualProvenance = buildFindingProvenance(Object.keys(reportEdits), 'manual');
  if (manualProvenance) {
    Object.assign(additionalInfo, manualProvenance);
  }

  if (Object.keys(additionalInfo).length > 0) {
    findingsData.additional_info = additionalInfo;
  }

  return findingsData;
};
